import classNames from "classnames";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import { Modal } from "PFComponents/modal";
import { isProfileActive } from "PFCore/helpers/profile";
import { useIsShortlistingPermitted } from "PFCore/helpers/shortlists";
import { useActivityUpdate } from "PFCore/hooks/queries/activity";
import { useShortlistReset } from "PFCore/hooks/queries/shortlists/use_shortlist_actions";
import { Activity, Shortlist as ShortlistType } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useActivityCurrentUserRole } from "../../../../activities/hooks";
import ButtonLabel from "../../../../activities/parts/button_label";
import { BookButton } from "../../../../activities/show/parts/book_button/book_button";
import { useBookButtonPermissions } from "../../../../activities/show/parts/book_button/use_book_button_permissions";
import ExceptionsWarning from "./exceptions_warning";
import css from "./shortlist_item_buttons.module.scss";

type RespondedTagProps = {
  activity: Activity;
  isAudit: boolean;
  item: ShortlistType;
  qaPrefix?: string;
};

const RespondedTag = ({ activity, isAudit, item, qaPrefix }: RespondedTagProps) => {
  const { mutate: resetShortlist } = useShortlistReset();
  const { update: updateActivity } = useActivityUpdate(activity?.id);

  const { t } = useTranslation(["activities", "translation"]);

  const { showGrowl } = useGrowlStoreActions();
  const navigate = useNavigate();

  const { isReviewer } = useActivityCurrentUserRole(activity);
  const canBook = useBookButtonPermissions({
    state: item.state,
    coowned: !!activity?.coowned,
    isReviewer
  });
  const isShortlistActive = isProfileActive(item.profile);
  const isBookButtonVisible = isShortlistActive && canBook;

  const isShortlistingPermitted = useIsShortlistingPermitted();

  const isFilledOrDeclined = item.state === "filled" || item.state === "declined";
  const hasException = activity?.workflow_state === "exception";
  const [displayUndo, setDisplayUndo] = useState(false);
  const canUndo = activity?.coowned; // only owner can undo filled role

  return (
    <div className={classNames(css.respondedButtons, { [css.bookButtonDisabled]: !isBookButtonVisible })}>
      {isBookButtonVisible && (
        <BookButton activity={activity} profileId={item.profile.id} shortlistId={item.id} />
      )}
      <div className={css.filledButtons}>
        {isAudit ? (
          activity?.coowned && (
            <>
              {hasException && <ExceptionsWarning />}
              <Button
                text={t("translation:undo")}
                kind="secondary"
                disabled={hasException}
                onClick={() => setDisplayUndo(true)}
                qaId={`${qaPrefix}-undo`}
              />
            </>
          )
        ) : (
          <>
            {canUndo && (
              <Button kind="blank" onClick={() => setDisplayUndo(true)} qaId={`${qaPrefix}-undo`}>
                {t("translation:undo")}
              </Button>
            )}
            {(isShortlistingPermitted(activity) || isReviewer) && (
              <ButtonLabel>
                {isFilledOrDeclined && <Icon name="user-filled" size="sm" />}
                {t(`activities:show.shortlist.state.${item.state}`)}
              </ButtonLabel>
            )}
          </>
        )}
      </div>

      {displayUndo && (
        <Modal
          title={
            isAudit
              ? t("activities:show.shortlist.unallocateResource")
              : t("activities:show.shortlist.unfillRole")
          }
          labelOK={t("translation:continueText")}
          onOK={() =>
            resetShortlist(item.id, {
              onSuccess: () => {
                /* @ts-ignore */
                updateActivity(); // this is needed to trigger refetching callback on audit planner
                showGrowl({
                  kind: "success",
                  message: isAudit
                    ? t("activities:show.shortlist.resourceUnallocated")
                    : t("activities:show.shortlist.roleUnfilled")
                });
                if (isAudit) {
                  navigate("/audit_planner");
                }
              },
              onError: () => {
                /* @ts-ignore */
                updateActivity();
              }
            })
          }
          onClose={() => {
            setDisplayUndo(false);
          }}
          kindOK="danger"
        >
          {isAudit
            ? t("activities:show.shortlist.wantUnallocateResource")
            : t("activities:show.shortlist.wantUnfillRole")}
        </Modal>
      )}
    </div>
  );
};

export default RespondedTag;
